import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Boxes,
  Truck,
  MapPin,
  ArrowRight,
  Tag,
  Layers,
  Sparkles,
  Clock,
  Weight,
  Anchor,
  Hand,
  Zap,
  Scissors,
  ListChecks,
  Boxes as StackBoxes,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison aux Alluets-le-Roi ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de fournitures directement chez vous
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que faire des kilomètres pour récupérer des
              cartons dans des zones commerciales encombrées est une
              perte de temps, notre service de livraison s&apos;invite
              sur le pas de votre porte.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Une couverture locale de confiance",
                body: "Nous acheminons l'intégralité de vos commandes à plat aux Alluets-le-Roi, mais aussi dans les villages et communes limitrophes comme Orgeval, Crespières, Maule, Morainvilliers et Ecquevilly.",
                Icon: MapPin,
              },
              {
                title: "Des engagements logistiques stricts",
                body: "Vous choisissez votre créneau horaire précis pour intégrer la livraison parfaitement dans votre emploi du temps. Toutes vos fournitures vous sont remises dans un état de propreté irréprochable, 100 % sèches et prêtes à être pliées pour attaquer vos pièces immédiatement.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons renforcés sécurité maximale ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Sécurité maximale
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Des cartons renforcés pour une sécurité maximale
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Ne prenez pas le risque de voir le fond d&apos;une boîte
            céder pendant le portage. Nos cartons de qualité
            industrielle font la différence.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-5">
          {[
            {
              title: "Double cannelure rigide",
              body: "Nos cartons d'emballage possèdent des parois renforcées qui résistent à la déformation, à la perforation et à l'humidité ambiante.",
              Icon: Layers,
              accent: "#237cb5",
            },
            {
              title: "Empilage stable dans le camion",
              body: "Grâce à leur robustesse, vous pouvez empiler les cartons lourds les uns sur les autres sur toute la hauteur du véhicule sans aucun risque d'affaissement pour les colis du dessous.",
              Icon: StackBoxes,
              accent: "#237cb5",
            },
            {
              title: "Poignées latérales indéchirables",
              body: "Conçus pour faciliter le travail des déménageurs ou de vos proches, nos modèles intègrent des zones de portage ergonomiques qui évitent les glissements et sécurisent la manutention.",
              Icon: Hand,
              accent: "#FF5722",
            },
          ].map(({ title, body, Icon, accent }) => (
            <div
              key={title}
              className="relative bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7 overflow-hidden"
            >
              <span
                className="absolute top-0 left-0 h-1.5 w-full"
                style={{ backgroundColor: accent }}
              />
              <span
                className="flex items-center justify-center h-12 w-12 rounded-xl text-white"
                style={{ backgroundColor: accent }}
              >
                <Icon className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <h3 className="mt-4 font-display font-extrabold text-[17px] text-[#0f1d2b] leading-tight">
                {title}
              </h3>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-les-alluets-le-roi-cta-renforces"
          >
            <Weight className="h-4 w-4" strokeWidth={2.6} />
            Voir les cartons renforcés
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Préparation simple et rapide ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Préparation express
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Une préparation plus simple et plus rapide avant le jour J
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Optimisez vos préparatifs et divisez votre temps de mise
            en boîte par deux grâce à une méthodologie simple et des
            accessoires adaptés.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6 relative">
          {/* ligne dégradée connectant les étapes */}
          <div
            aria-hidden="true"
            className="hidden lg:block absolute top-12 left-[16.66%] right-[16.66%] h-0.5 bg-gradient-to-r from-[#237cb5] via-[#237cb5] to-[#FF5722]"
          />
          {[
            {
              num: "01",
              title: "Le pliage instantané",
              body: "Nos cartons professionnels se déploient au millimètre près en deux secondes, sans forcer sur les rabats, garantissant une structure parfaitement d'équerre.",
              Icon: Zap,
              color: "#237cb5",
            },
            {
              num: "02",
              title: "Le scellage en un mouvement",
              body: "En utilisant notre dévidoir pistolet pro, vous fermez et sécurisez le fond et le dessus de vos boîtes d'un seul geste fluide, sans jamais chercher le bout du rouleau d'adhésif.",
              Icon: Scissors,
              color: "#237cb5",
            },
            {
              num: "03",
              title: "Le repérage immédiat",
              body: "Grâce aux grilles de marquage pré-imprimées sur nos cartons, vous cochez la pièce de destination (Cuisine, Salon, Chambre 1) et listez le contenu en un clin d'œil pour faciliter le déballage à l'arrivée.",
              Icon: ListChecks,
              color: "#FF5722",
            },
          ].map(({ num, title, body, Icon, color }) => (
            <div key={num} className="relative z-10">
              <div className="flex justify-center">
                <span
                  className="flex items-center justify-center h-24 w-24 rounded-full border-[6px] border-white text-white font-display font-extrabold text-[28px] shadow-lg"
                  style={{ backgroundColor: color }}
                >
                  {num}
                </span>
              </div>
              <div className="mt-6 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 text-center">
                <div className="flex justify-center">
                  <span
                    className="flex items-center justify-center h-11 w-11 rounded-xl text-white"
                    style={{ backgroundColor: color }}
                  >
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                </div>
                <h3 className="mt-4 font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-3 text-[13.5px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-les-alluets-le-roi-cta-pistolet"
          >
            <Scissors className="h-4 w-4" strokeWidth={2.6} />
            Voir le dévidoir pistolet
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/kits-cartons"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-les-alluets-le-roi-cta-kits"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Découvrir les kits
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-les-alluets-le-roi-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
